"use client"

import { useState, useEffect } from "react"
import { X, Zap, Users, Target, Calendar, ChevronDown, Check, BarChart2, AlertCircle } from "lucide-react"
import { useProjects } from "../contexts/ProjectContext"
import api from "../utils/apiClient"

const KeywordGenerationModal = ({ isOpen, onClose, onGenerate, isLoading }) => {
  const { currentProject } = useProjects()
  const [generationType, setGenerationType] = useState("competitors-audiences")
  const [competitors, setCompetitors] = useState([])
  const [targetAudiences, setTargetAudiences] = useState([])
  const [numKeywords, setNumKeywords] = useState(7)
  const [competitorDropdownOpen, setCompetitorDropdownOpen] = useState(false)
  const [audienceDropdownOpen, setAudienceDropdownOpen] = useState(false)
  
  // For error handling
  const [errorMessage, setErrorMessage] = useState('')
  const [suggestionsCount, setSuggestionsCount] = useState(0)

  useEffect(() => {
    // Reset state when modal opens
    if (isOpen) {
      setErrorMessage('')
      checkExistingSuggestions()
    }
  }, [isOpen, currentProject])

  const checkExistingSuggestions = async () => {
    if (!currentProject?.id) return
    
    try {
      // Add a loading indicator
      const response = await api.keywords.getSuggestions(currentProject.id)
      if (response.data && response.data.length > 0) {
        setSuggestionsCount(response.data.length)
      }
    } catch (error) {
      // Silently fail
    }
  }

  if (!isOpen) return null

  const handleCompetitorToggle = (value) => {
    if (value === "all") {
      setCompetitors(competitors.includes("all") ? [] : ["all"])
      return
    }
    if (competitors.includes("all")) {
      setCompetitors([value])
      return
    }
    setCompetitors((prev) => (prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]))
  }

  const handleTargetAudienceToggle = (value) => {
    if (value === "all") {
      setTargetAudiences(targetAudiences.includes("all") ? [] : ["all"])
      return
    }
    if (targetAudiences.includes("all")) {
      setTargetAudiences([value])
      return
    }
    setTargetAudiences((prev) => (prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]))
  }

  const getCompetitorDisplayText = () => {
    if (competitors.length === 0) return "Select competitors..."
    if (competitors.includes("all")) return "All Competitors"
    if (competitors.length === 1) {
      const competitor = currentProject?.project_competitors?.find((c) => (c.id || c.url) === competitors[0])
      return competitor?.name || competitor?.url || competitors[0]
    }
    return `${competitors.length} competitors selected`
  }

  const getAudienceDisplayText = () => {
    if (targetAudiences.length === 0) return "Select target audiences..."
    if (targetAudiences.includes("all")) return "All Target Audiences"
    if (targetAudiences.length === 1) {
      const audience = currentProject?.target_audiences?.find((a) => (a.id || a) === targetAudiences[0])
      return audience?.name || audience || targetAudiences[0]
    }
    return `${targetAudiences.length} audiences selected`
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrorMessage('')
    
    try {
      onGenerate({
        numKeywords,
      })
    } catch (error) {
      setErrorMessage(error.response?.data?.error || "Failed to process request")
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl transform transition-all duration-300 ease-out max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="relative px-8 pt-8 pb-6 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Zap className="w-5 h-5 text-primary-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Generate Keywords</h2>
          </div>
          <p className="text-gray-600 text-sm">
            Generate keywords using your seed keywords database and schedule them in your content calendar
          </p>
          <button
            onClick={onClose}
            className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
          >
            <X size={20} />
          </button>
        </div>

        {/* Error message */}
        {errorMessage && (
          <div className="mx-8 mt-4 p-3 bg-red-50 border border-red-100 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        )}

        {/* Information about existing suggestions */}
        {suggestionsCount > 0 && (
          <div className="mx-8 mt-4 p-3 bg-blue-50 border border-blue-100 rounded-lg flex items-start gap-2">
            <BarChart2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-700">
              You have {suggestionsCount} keyword suggestions available. Select how many keywords to schedule below.
            </p>
          </div>
        )}

        {/* Combined Form */}
        <form onSubmit={handleSubmit} className="px-8 py-6 space-y-6">
          {/* Generation Type */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-primary-600" />
              <label className="text-sm font-semibold text-gray-800">Generation Strategy</label>
            </div>
            <div className="relative">
              <select
                value={generationType}
                onChange={(e) => setGenerationType(e.target.value)}
                className="block w-full pl-4 pr-10 py-3 text-base border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent rounded-xl bg-gray-50 hover:bg-white transition-all duration-200 appearance-none"
              >
                <option value="competitors">Competitors</option>
                <option value="target-audiences">Target Audiences</option>
                <option value="competitors-audiences">Competitors & Target Audiences</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <ChevronDown className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>

          {/* Select Competitors */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-primary-600" />
              <label className="text-sm font-semibold text-gray-800">Competitor Selection</label>
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => setCompetitorDropdownOpen(!competitorDropdownOpen)}
                className="block w-full pl-4 pr-10 py-3 text-base border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent rounded-xl bg-gray-50 hover:bg-white transition-all duration-200 text-left"
              >
                <span className={competitors.length === 0 ? "text-gray-500" : "text-gray-900"}>
                  {getCompetitorDisplayText()}
                </span>
              </button>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${competitorDropdownOpen ? "rotate-180" : ""}`}
                />
              </div>

              {competitorDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                  <div className="p-1">
                    <label className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={competitors.includes("all")}
                        onChange={() => handleCompetitorToggle("all")}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium text-gray-800">All Competitors</span>
                      {competitors.includes("all") && <Check className="w-4 h-4 text-primary-600 ml-auto" />}
                    </label>
                    {currentProject?.project_competitors?.map((competitor) => (
                      <label
                        key={competitor.id || competitor.url}
                        className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={competitors.includes(competitor.id || competitor.url)}
                          onChange={() => handleCompetitorToggle(competitor.id || competitor.url)}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 flex-1 truncate">{competitor.name || competitor.url}</span>
                        {competitors.includes(competitor.id || competitor.url) && (
                          <Check className="w-4 h-4 text-primary-600" />
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Select Target Audiences */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-primary-600" />
              <label className="text-sm font-semibold text-gray-800">Target Audience Selection</label>
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => setAudienceDropdownOpen(!audienceDropdownOpen)}
                className="block w-full pl-4 pr-10 py-3 text-base border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent rounded-xl bg-gray-50 hover:bg-white transition-all duration-200 text-left"
              >
                <span className={targetAudiences.length === 0 ? "text-gray-500" : "text-gray-900"}>
                  {getAudienceDisplayText()}
                </span>
              </button>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${audienceDropdownOpen ? "rotate-180" : ""}`}
                />
              </div>

              {audienceDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                  <div className="p-1">
                    <label className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={targetAudiences.includes("all")}
                        onChange={() => handleTargetAudienceToggle("all")}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium text-gray-800">All Target Audiences</span>
                      {targetAudiences.includes("all") && <Check className="w-4 h-4 text-primary-600 ml-auto" />}
                    </label>
                    {currentProject?.target_audiences?.map((audience) => (
                      <label
                        key={audience.id || audience}
                        className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={targetAudiences.includes(audience.id || audience)}
                          onChange={() => handleTargetAudienceToggle(audience.id || audience)}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 flex-1 truncate">{audience.name || audience}</span>
                        {targetAudiences.includes(audience.id || audience) && (
                          <Check className="w-4 h-4 text-primary-600" />
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Number of Keywords */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-primary-600" />
              <label className="text-sm font-semibold text-gray-800">Number of Keywords</label>
            </div>
            <div className="relative">
              <input
                type="number"
                value={numKeywords}
                onChange={(e) => setNumKeywords(Math.max(1, Math.min(30, Number.parseInt(e.target.value) || 1)))}
                className="block w-full pl-4 pr-4 py-3 text-base border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent rounded-xl bg-gray-50 hover:bg-white transition-all duration-200"
                min="1"
                max="30"
                placeholder="Enter number of keywords"
              />
            </div>
            <div className="flex items-center gap-2 px-4 py-3 bg-primary-50 rounded-xl border border-primary-100">
              <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
              <p className="text-sm text-primary-700">
                Each keyword will be scheduled for content creation on consecutive days on your calendar.
              </p>
            </div>
          </div>

          {/* Buttons */}
          <div className="pt-4 border-t border-gray-100 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-5 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isLoading ? "Generating..." : "Generate Keywords"}
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Zap className="w-4 h-4" />
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default KeywordGenerationModal;
